import React, { useState, useEffect, useCallback } from 'react';
import { format, subDays } from 'date-fns';
import ReportLayout, { ReportTable, usePagination } from './ReportLayout';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { fmtTime12 } from '../../utils/time';

const fmtDT = v => { try { return format(new Date(v), 'd MMM yy h:mm a'); } catch { return v || '–'; } };
const statusBadge = v => {
  const map = { approved:'bg-green-100 text-green-700', pending:'bg-amber-100 text-amber-700', rejected:'bg-red-100 text-red-700' };
  return <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium capitalize', map[v] || 'bg-slate-100 text-slate-600')}>{v || '–'}</span>;
};

const SHIFT_TYPES = ['Standard', 'Active Night', 'Sleepover', 'Community Access', 'Transport', 'Admin'];

const COLS = [
  { key: 'shift_date',       label: 'Shift Date' },
  { key: 'staff_name',       label: 'Staff Name' },
  { key: 'client_names',     label: 'Client(s)' },
  { key: 'shift_type',       label: 'Shift Type' },
  { key: 'rostered_start',   label: 'Rostered Start', render: fmtTime12 },
  { key: 'rostered_end',     label: 'Rostered End',   render: fmtTime12 },
  { key: 'rostered_hours',   label: 'Rostered Hrs', render: v => v ? `${parseFloat(v).toFixed(2)}h` : '–' },
  { key: 'clock_in_time',    label: 'Clock In',     render: fmtDT },
  { key: 'clock_out_time',   label: 'Clock Out',    render: fmtDT },
  { key: 'hours_worked',     label: 'Actual Hrs',   render: v => v ? `${parseFloat(v).toFixed(2)}h` : '–' },
  { key: 'break_minutes',    label: 'Break (min)',  render: v => v || '0' },
  { key: 'timesheet_status', label: 'Status',       render: statusBadge },
];

export default function TimesheetQuery() {
  const [data, setData]           = useState([]);
  const [staff, setStaff]         = useState([]);
  const [clients, setClients]     = useState([]);
  const [loading, setLoading]     = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [startDate, setStart]     = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [endDate, setEnd]         = useState(format(new Date(), 'yyyy-MM-dd'));
  const [staffId, setStaffId]     = useState('');
  const [clientId, setClientId]   = useState('');
  const [status, setStatus]       = useState('');
  const [shiftType, setShiftType] = useState('');
  const [searched, setSearched]   = useState(false);
  const pagination = usePagination(data);

  useEffect(() => {
    api.get('/staff').then(r => setStaff(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get('/clients').then(r => setClients(Array.isArray(r.data) ? r.data : r.data?.clients || [])).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    setSearched(true);
    const p = new URLSearchParams({ start_date: startDate, end_date: endDate });
    if (staffId)   p.set('staff_id', staffId);
    if (clientId)  p.set('client_id', clientId);
    if (status)    p.set('status', status);
    api.get(`/reports/timesheet-report?${p}`)
      .then(r => {
        let rows = r.data || [];
        if (shiftType) rows = rows.filter(row => row.shift_type === shiftType);
        setData(rows);
        pagination.reset();
      })
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, [startDate, endDate, staffId, clientId, status, shiftType]);

  const selStaff  = staff.find(s => s.id === staffId);
  const selClient = clients.find(c => c.id === clientId);
  const chips = [
    { label: 'Date Range', value: `${startDate} — ${endDate}` },
    ...(selStaff   ? [{ label: 'Staff',      value: `${selStaff.first_name} ${selStaff.last_name}`,   onRemove: () => setStaffId('') }]   : []),
    ...(selClient  ? [{ label: 'Client',     value: `${selClient.first_name} ${selClient.last_name}`, onRemove: () => setClientId('') }]  : []),
    ...(status     ? [{ label: 'Status',     value: status,    onRemove: () => setStatus('') }]    : []),
    ...(shiftType  ? [{ label: 'Shift Type', value: shiftType, onRemove: () => setShiftType('') }] : []),
  ];

  return (
    <ReportLayout title="Timesheet Query Report" data={data} filename="timesheet_query"
      columns={COLS} activeChips={chips} showFiltersPanel={showFilters} onToggleFilters={() => setShowFilters(v => !v)}
      filters={
        <div className="space-y-4">
          <div className="flex flex-wrap gap-4">
            <div><label className="block text-xs font-semibold text-slate-500 mb-1">Start Date</label>
              <input type="date" value={startDate} onChange={e => setStart(e.target.value)} className="input-field !w-auto text-sm" /></div>
            <div><label className="block text-xs font-semibold text-slate-500 mb-1">End Date</label>
              <input type="date" value={endDate} onChange={e => setEnd(e.target.value)} className="input-field !w-auto text-sm" /></div>
            <div><label className="block text-xs font-semibold text-slate-500 mb-1">Staff</label>
              <select value={staffId} onChange={e => setStaffId(e.target.value)} className="input-field !w-auto text-sm">
                <option value="">All Staff</option>
                {staff.map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
              </select></div>
            <div><label className="block text-xs font-semibold text-slate-500 mb-1">Client</label>
              <select value={clientId} onChange={e => setClientId(e.target.value)} className="input-field !w-auto text-sm">
                <option value="">All Clients</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
              </select></div>
            <div><label className="block text-xs font-semibold text-slate-500 mb-1">Shift Type</label>
              <select value={shiftType} onChange={e => setShiftType(e.target.value)} className="input-field !w-auto text-sm">
                <option value="">All Types</option>
                {SHIFT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select></div>
            <div><label className="block text-xs font-semibold text-slate-500 mb-1">Status</label>
              <select value={status} onChange={e => setStatus(e.target.value)} className="input-field !w-auto text-sm">
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select></div>
            <div className="flex items-end">
              <button onClick={load} className="btn-primary text-sm px-6 py-2">Run Query</button>
            </div>
          </div>
        </div>
      }>
      {!searched ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center text-slate-400 text-sm">
          Set your filters above and click <strong>Run Query</strong> to view timesheets.
        </div>
      ) : (
        <ReportTable columns={COLS} data={data} loading={loading} pagination={pagination} />
      )}
    </ReportLayout>
  );
}
